import React, { useEffect, useState, useCallback } from "react";
import { ArrowRight, ArrowLeft, AlertTriangle, Ban, RefreshCcw, ShieldX, CheckCircle2, Info, X } from "lucide-react";
import { api } from "../lib/api.js";
import StatusBadge from "./StatusBadge.jsx";

const SPECIAL_ICON = {
  Cancelado: <Ban className="w-4 h-4" />,
  Reaberto: <RefreshCcw className="w-4 h-4" />,
  Rejeitado: <ShieldX className="w-4 h-4" />,
};

const SPECIAL_TONE = {
  Cancelado: "border-[#DC3545] text-[#DC3545] hover:bg-[#DC3545]/10",
  Reaberto: "border-[#1F3864] text-[#1F3864] hover:bg-[#1F3864]/10",
  Rejeitado: "border-[#856404] text-[#856404] hover:bg-[#FFF7E6]",
};

function WashQuestion({ onChoose, submitting }) {
  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F4F6F8] border border-gray-200 rounded-sm"
      data-testid="workflow-wash-question"
    >
      <span className="text-xs text-[#1A1A2E] font-medium">Esta viatura precisa de lavagem?</span>
      <button
        type="button"
        onClick={() => onChoose(true)}
        disabled={submitting}
        className="px-3 py-1 bg-[#1F3864] text-white rounded-sm text-xs hover:bg-[#16284a] disabled:opacity-60 font-semibold"
        data-testid="workflow-wash-yes"
      >Sim — avançar para Em lavagem</button>
      <button
        type="button"
        onClick={() => onChoose(false)}
        disabled={submitting}
        className="px-3 py-1 border border-[#1F3864] text-[#1F3864] rounded-sm text-xs hover:bg-[#1F3864]/10 disabled:opacity-60 font-semibold"
        data-testid="workflow-wash-no"
      >Não — saltar para Pronto para entrega</button>
    </div>
  );
}

function FullSequenceBar({ sequence, labels, meta, currentIndex, currentStatus }) {
  const wrapRef = React.useRef(null);
  const currentRef = React.useRef(null);

  // Auto-scroll the current dot into the centre of the viewport on mount /
  // whenever the current index changes.
  React.useEffect(() => {
    const el = currentRef.current;
    const wrap = wrapRef.current;
    if (!el || !wrap) return;
    const targetLeft = el.offsetLeft - wrap.clientWidth / 2 + el.clientWidth / 2;
    wrap.scrollTo({ left: Math.max(0, targetLeft), behavior: "smooth" });
  }, [currentIndex, sequence?.length]);

  if (!sequence?.length) return null;

  // Iter 62 · Fix 2 — item width fixo por passo (76px) + connector fixo (24px).
  // Rack unificado alinha dots numa linha só; a label vai por baixo com
  // altura máxima definida para nunca empurrar o rack.
  const ITEM_W = 76;
  const CONN_W = 24;

  return (
    <div className="mb-4 relative" data-testid="workflow-full-sequence">
      <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-white to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-white to-transparent z-10" />
      <div ref={wrapRef} className="overflow-x-auto pb-2 px-1 scroll-smooth">
        <div className="flex items-start" style={{ minWidth: "max-content" }}>
          {sequence.map((s, i) => {
            const passed = i < currentIndex;
            const isCurrent = currentStatus === s || (i === currentIndex && currentIndex >= 0);
            const m = meta?.[i] || {};
            const isTerminal = !!m.terminal;
            const isAuto = !!m.auto_set;
            const isDivergent = s === "Rejeitado pelo cliente";
            let dotCls;
            let dotSize = "w-2.5 h-2.5"; // 10px inactive
            if (isDivergent && isCurrent) {
              dotCls = "bg-[#DC3545] ring-4 ring-[#DC3545]/20";
              dotSize = "w-3.5 h-3.5"; // 14px current
            } else if (isDivergent) dotCls = "bg-[#DC3545]/40";
            else if (isCurrent) {
              dotCls = "bg-[#1F3864] ring-4 ring-[#1F3864]/20";
              dotSize = "w-3.5 h-3.5";
            } else if (passed) dotCls = "bg-[#28A745]";
            else if (isTerminal) dotCls = "bg-[#94A3B8]";
            else dotCls = "bg-gray-300";
            const lineCls = i <= currentIndex ? "bg-[#28A745]" : "bg-gray-200";
            const labelTone = isDivergent && isCurrent ? "text-[#DC3545] font-semibold"
              : isCurrent ? "text-[#1F3864] font-semibold"
              : passed ? "text-[#28A745]"
              : "text-[#94A3B8]";
            const displayLabel = (labels && labels[i]) || s;
            return (
              <div
                key={s}
                ref={isCurrent ? currentRef : null}
                className="flex flex-col items-center flex-none"
                style={{ width: `${ITEM_W}px` }}
              >
                {/* Rack de dots + linhas: altura fixa 20px para todos os itens */}
                <div className="relative flex items-center justify-center w-full" style={{ height: "20px" }}>
                  {i > 0 && (
                    <div
                      className={`absolute top-1/2 -translate-y-1/2 h-0.5 ${lineCls}`}
                      style={{ right: "50%", width: `${ITEM_W / 2 + CONN_W / 2}px`, marginRight: `${CONN_W / 2}px` }}
                      aria-hidden="true"
                    />
                  )}
                  <div className="relative z-[1]">
                    <div
                      className={`${dotSize} rounded-full ${dotCls}`}
                      data-testid={`state-dot-${i}`}
                      title={isAuto ? `${s} (definido automaticamente)` : s}
                    />
                    {isAuto && (
                      <span
                        className="absolute -top-1 -right-1 text-[#856404] bg-[#FFF7E6] border border-[#FFC107]/60 rounded-full leading-none"
                        style={{ fontSize: "8px", padding: "1px 2px" }}
                        data-testid={`state-auto-mark-${i}`}
                        title="Definido automaticamente"
                      >A</span>
                    )}
                  </div>
                </div>
                {/* Label: sempre por baixo do dot, altura própria não afecta o rack */}
                <div
                  className={`text-center leading-tight ${labelTone} mt-1`}
                  style={{
                    fontSize: "9px",
                    maxWidth: `${ITEM_W - 8}px`,
                    wordBreak: "break-word",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                  title={s}
                >
                  {displayLabel}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ open, title, label, target, requiresReason, requiresDate, onCancel, onConfirm }) {
  const [comment, setComment] = useState("");
  const [dateValue, setDateValue] = useState("");
  useEffect(() => {
    if (open) {
      setComment("");
      setDateValue("");
    }
  }, [open]);
  if (!open) return null;
  const placeholder = requiresReason ? "Motivo (obrigatório)…" : "Comentário (obrigatório)…";
  const submitDisabled = !comment.trim() || (requiresDate && !dateValue);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onCancel} data-testid="workflow-confirm-modal">
      <div className="bg-white rounded-md border border-gray-200 w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-heading text-base font-bold text-[#1A1A2E]">{title}</h3>
          <button onClick={onCancel} className="text-[#6C757D] hover:text-[#1A1A2E]" data-testid="workflow-confirm-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="text-sm text-[#1A1A2E] mb-3">
          {label} <span className="font-mono font-semibold">{target}</span>
        </div>
        {requiresDate && (
          <label className="block mb-3">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Data agendada para a entrada *</span>
            <input
              type="date"
              value={dateValue}
              onChange={(e) => setDateValue(e.target.value)}
              className="w-full mt-0.5 px-2 py-1.5 border border-gray-200 rounded-sm text-sm focus:border-[#1F3864] outline-none"
              data-testid="workflow-confirm-date"
            />
          </label>
        )}
        <textarea
          className="w-full border border-gray-200 rounded-sm px-3 py-2 text-sm focus:border-[#1F3864] outline-none min-h-[90px]"
          placeholder={placeholder}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          data-testid="workflow-confirm-comment"
        />
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 border border-gray-200 rounded-sm text-sm hover:border-[#1A1A2E]"
            data-testid="workflow-confirm-cancel"
          >Cancelar</button>
          <button
            onClick={() => onConfirm(comment, dateValue || null)}
            disabled={submitDisabled}
            className="px-3 py-1.5 bg-[#1F3864] text-white rounded-sm text-sm disabled:opacity-50 hover:bg-[#16284a]"
            data-testid="workflow-confirm-submit"
          >Confirmar</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Progressive status control.
 *
 * Props:
 *  - entity: "som" | "pec" | "pcp" | "swc"
 *  - recordId: string
 *  - currentStatus: string
 *  - onAdvance: async (newStatus, comment, opts) => void  — caller wires this to its own status PATCH/POST
 *  - extraInfo: { auto_set_link: { to: string, label: string } | null }
 *      Optional info shown when current state is auto-set by another module.
 */
export default function ProgressiveStatusControl({ entity, recordId, currentStatus, onAdvance, extraInfo }) {
  const [trans, setTrans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const ROUTES = {
    som: `/service-cases/${recordId}/allowed-transitions`,
    pec: `/parts-requests/${recordId}/allowed-transitions`,
    pcp: `/classification-requests/${recordId}/allowed-transitions`,
    swc: `/warranty-claims/${recordId}/allowed-transitions`,
  };

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(ROUTES[entity]);
      setTrans(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao carregar transições.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, recordId, currentStatus]);

  useEffect(() => { reload(); }, [reload]);

  const handleConfirm = async (comment, appointmentDate) => {
    if (!confirm || !comment.trim()) return;
    setSubmitting(true);
    try {
      await onAdvance(confirm.target, comment, {
        back: confirm.isBack,
        special: confirm.special,
        appointment_date: appointmentDate || undefined,
      });
      setConfirm(null);
      await reload();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao mudar estado.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-sm text-[#6C757D]" data-testid="workflow-loading">A carregar…</div>;
  if (error && !trans) return <div className="text-sm text-[#DC3545]" data-testid="workflow-error">{error}</div>;
  if (!trans) return null;

  const { primary, branches, back, back_allowed, special, terminal, auto_set, milestones, current_milestone_index, full_sequence, current_full_index, sequence_meta, short_labels } = trans;

  const CRITICAL_BY_ENTITY = {
    som: new Set(["Cancelado", "Rejeitado pelo cliente", "Encerrado", "Reaberto"]),
    pec: new Set(["Cancelado", "Marcação falhada", "Reaberto"]),
    pcp: new Set(["Cancelado", "Rejeitado", "Reaberto"]),
    swc: new Set(["Cancelado", "Não aprovado", "Reaberto", "Encerrado"]),
  };
  const criticalSet = CRITICAL_BY_ENTITY[entity] || new Set();

  // Routine forward moves skip the modal entirely; only critical targets,
  // back-steps and special actions open the comment modal.
  const tryAdvance = async ({ target, label, isBack = false, special: spec = false, defaultComment = "" }) => {
    const isCritical = criticalSet.has(target);
    const needsModal = isBack || spec || isCritical;
    if (needsModal) {
      setConfirm({ target, label, isBack, special: spec });
      return;
    }
    // Fast path — no modal, immediate transition with default audit note.
    setSubmitting(true);
    try {
      await onAdvance(target, defaultComment, { back: false, special: false });
      await reload();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao mudar estado.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md p-4" data-testid="progressive-status-control">
      <FullSequenceBar
        sequence={full_sequence || milestones}
        labels={short_labels}
        meta={sequence_meta}
        currentIndex={current_full_index ?? current_milestone_index}
        currentStatus={currentStatus}
      />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Estado actual</span>
          <StatusBadge status={currentStatus} />
        </div>

        <div className="flex-1 flex flex-wrap items-center gap-2 justify-end">
          {terminal && (
            <span className="text-xs text-[#6C757D] italic" data-testid="workflow-terminal-notice">
              Este é um estado terminal. Para retomar, use “Reabrir”.
            </span>
          )}

          {auto_set && extraInfo?.auto_set_link && (
            <div className="text-xs text-[#856404] bg-[#FFF7E6] border border-[#FFC107]/40 rounded-sm px-3 py-1.5" data-testid="workflow-auto-set-notice">
              <Info className="w-3 h-3 inline-block mr-1" />
              Estado actualizado automaticamente pelo sistema.{" "}
              <a href={extraInfo.auto_set_link.to} className="font-semibold underline">{extraInfo.auto_set_link.label}</a>
            </div>
          )}

          {/* Special case: wash question only for SOM at Controlo de qualidade */}
          {entity === "som" && currentStatus === "Controlo de qualidade" && primary && !auto_set ? (
            <WashQuestion
              onChoose={(needsWash) => {
                const target = needsWash ? "Em lavagem" : "Pronto para entrega";
                const label = needsWash ? "Avançar para" : "Saltar lavagem — avançar para";
                const defaultComment = needsWash
                  ? ""
                  : "Lavagem dispensada — viatura avançou directamente para Pronto para entrega";
                tryAdvance({ target, label, defaultComment });
              }}
              submitting={submitting}
            />
          ) : (
            <>
              {primary && !auto_set && (
                <button
                  type="button"
                  onClick={() => tryAdvance({ target: primary.status, label: "Avançar para" })}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#1F3864] text-white rounded-sm hover:bg-[#16284a] text-sm font-semibold disabled:opacity-60"
                  data-testid="workflow-primary-btn"
                >
                  Avançar para: <span className="font-mono">{primary.status}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}

              {!auto_set && branches?.map((b) => (
                <button
                  key={b.status}
                  type="button"
                  onClick={() => tryAdvance({ target: b.status, label: "Mudar para o estado" })}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-[#1F3864] text-[#1F3864] rounded-sm hover:bg-[#1F3864]/10 text-sm font-semibold disabled:opacity-60"
                  data-testid={`workflow-branch-btn-${b.status}`}
                >
                  {b.status}
                </button>
              ))}
            </>
          )}

          {special?.map((s) => (
            <button
              key={s.status}
              type="button"
              onClick={() => tryAdvance({ target: s.status, label: "Marcar como", special: true })}
              disabled={submitting}
              className={`inline-flex items-center gap-1.5 px-3 py-2 border rounded-sm text-sm font-semibold disabled:opacity-60 ${SPECIAL_TONE[s.status] || "border-gray-300 text-[#1A1A2E]"}`}
              data-testid={`workflow-special-btn-${s.status}`}
              title={s.requires_reason ? "Requer motivo" : undefined}
            >
              {SPECIAL_ICON[s.status]}
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {back && back_allowed && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <button
            type="button"
            onClick={() => tryAdvance({ target: back, label: "Voltar para o estado anterior", isBack: true })}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 text-xs text-[#6C757D] hover:text-[#1F3864] disabled:opacity-60"
            data-testid="workflow-back-btn"
          >
            <ArrowLeft className="w-3 h-3" />
            Voltar um passo (para <span className="font-mono ml-1">{back}</span>)
            <span className="ml-2 text-[10px] uppercase tracking-widest text-[#94A3B8]">Manager+</span>
          </button>
        </div>
      )}

      {back && !back_allowed && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-[11px] text-[#94A3B8]" data-testid="workflow-back-locked">
          <AlertTriangle className="w-3 h-3 inline-block mr-1" />
          Apenas managers podem retroceder o estado.
        </div>
      )}

      {error && trans && (
        <div className="mt-3 text-xs text-[#DC3545]" data-testid="workflow-mutation-error">{error}</div>
      )}

      <ConfirmModal
        open={!!confirm}
        title={confirm?.isBack ? "Voltar estado" : confirm?.special ? "Acção especial" : "Confirmar avanço"}
        label={confirm?.label || ""}
        target={confirm?.target || ""}
        requiresReason={confirm?.isBack || confirm?.special}
        requiresDate={entity === "pec" && confirm?.target === "Entrada agendada"}
        onCancel={() => !submitting && setConfirm(null)}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
